import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Paper,
} from "@mui/material";
import apiService from "../../../../../../service/Api";


export interface DonationResponse {
  id: number;
  donor_name: string | null;
  donor_email: string | null;
  donor_phone: string | null;
  transaction_amount: string;
  transaction_status: string | null;
  paynow_reference: string | null;
  paynow_status_raw?: Record<string, unknown>; // JSON field that might contain payment method
  created_at: string;
  currency?: string;
  payment_method?: string;
}

interface DonationsTableProps {
  bookingId?: number;
  title?: string;
  paid?: boolean;
  currency?: string;
  ordering?: string;
}

const DonationsTable: React.FC<DonationsTableProps> = ({
  bookingId,
  title = "All Donations",
  paid,
  currency,
  ordering = "-created_at",
}) => {
  const [donations, setDonations] = useState<DonationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDonations = async () => {
      if (!bookingId) return;

      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          booking_id: bookingId,
        };

        if (paid !== undefined) {
          params.paid = paid.toString();
        }
        if (currency) {
          params.currency = currency;
        }
        if (ordering) {
          params.ordering = ordering;
        }

        const response = await apiService.get<DonationResponse[] | { results: DonationResponse[] }>(
          "/api/chema/donations/",
          params
        );

        // Handle both paginated and non-paginated responses
        const data = response.data;
        if (Array.isArray(data)) {
          setDonations(data);
        } else if (data && 'results' in data) {
          setDonations(data.results);
        } else {
          setDonations([]);
        }
      } catch (err: unknown) {
        console.error("Error fetching donations:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch donations";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDonations();
  }, [bookingId, paid, currency, ordering]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}/${month}/${day}`;
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return { bg: "#f3f4f6", color: "#374151" };
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes("paid") || statusLower.includes("received") || statusLower === "paid") {
      return { bg: "#d1fae5", color: "#065f46" };
    }
    if (statusLower.includes("pending")) {
      return { bg: "#fef3c7", color: "#92400e" };
    }
    if (statusLower.includes("failed") || statusLower.includes("cancelled")) {
      return { bg: "#fee2e2", color: "#991b1b" };
    }
    return { bg: "#d1fae5", color: "#065f46" };
  };

  const formatPaymentMethod = (donation: DonationResponse) => {
    // First try the direct payment_method field
    if (donation.payment_method) {
      return formatMethodName(donation.payment_method);
    }

    // Try to extract from paynow_status_raw JSON
    if (donation.paynow_status_raw) {
      try {
        const raw = typeof donation.paynow_status_raw === 'string' 
          ? JSON.parse(donation.paynow_status_raw) 
          : donation.paynow_status_raw;
        
        // Paynow might store payment method in various fields
        const method = raw.payment_method || raw.method || raw.paymethod || raw.paymentMethod;
        if (method) {
          return formatMethodName(method);
        }
      } catch {
        // If parsing fails, continue to fallback
      }
    }

    return "N/A";
  };

  const formatMethodName = (method: string) => {
    // Map common payment method names
    const methodMap: Record<string, string> = {
      ecocash: "Ecocash",
      onemoney: "OneMoney",
      omari: "O'mari",
      innbucks: "InnBucks",
      visa: "Visa",
      mastercard: "Mastercard",
      zimswitch: "ZimSwitch",
      bank: "Bank",
      "my cash": "My Cash",
      mukuru: "Mukuru",
    };

    const methodLower = method.toLowerCase();
    return methodMap[methodLower] || method;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        {title}
      </Typography>

      {donations.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No donations found</Typography>
        </Box>
      ) : (
        <Table
          sx={{
            "& td, & th": {
              borderBottom: "1px solid #e5e7eb",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Donor Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Payment Method</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {donations.map((donation) => (
              <TableRow key={donation.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#9ca3af",
                      }}
                    />
                    {formatDate(donation.created_at)}
                  </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 500 }}>
                  {donation.donor_name || "Anonymous"}
                </TableCell>

                <TableCell>
                  <Chip
                    label={donation.transaction_status || "Received"}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(donation.transaction_status).bg,
                      color: getStatusColor(donation.transaction_status).color,
                      fontWeight: 500,
                      borderRadius: "12px",
                    }}
                  />
                </TableCell>

                <TableCell>
                  {formatPaymentMethod(donation)}
                </TableCell>

                <TableCell sx={{ fontWeight: "bold" }}>
                  ${parseFloat(donation.transaction_amount || "0").toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default DonationsTable;